import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import queryString, { StringifyOptions } from 'query-string';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  EfbFuelDetailsRecord,
  FlightGetRecord,
  FlightSearchRecord,
  GetFuelDataRequest,
  UpdateEFBRequest,
  UpdateFuelReceiptRequest,
  FuelSupplierDetailsRecord,
} from '../types';
import { FlightAttachemnt } from '../types/flight-attachment';
import { CalculateEFBRequest } from '../types/calculate-efb-request';
import { CalculateEfbResponse } from '../types/calculate-efb-response';

@Injectable({
  providedIn: 'root',
})
export class FuelDataService {
  sectionIsEdited$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private fsdUpdatedSource = new Subject<void>();
  fsdUpdated$ = this.fsdUpdatedSource.asObservable();

  private efbUpdatedSource = new Subject<void>();
  efbUpdated$ = this.efbUpdatedSource.asObservable();
  constructor(private http: HttpClient) {}

  notifyFsdUpdated(): void {
    this.fsdUpdatedSource.next();
  }

  notifyEfbUpdated(): void {
    this.efbUpdatedSource.next();
  }

  search(request: GetFuelDataRequest): Observable<FlightSearchRecord[]> {
    const options: StringifyOptions = {
      skipNull: true,
    };

    return this.http.get<FlightSearchRecord[]>(
      `/api/fueldata?${queryString.stringify(request, options)}`
    );
  }

  getFlightDetails(id: number): Observable<FlightGetRecord> {
    return this.http.get<FlightGetRecord>(`/api/flight/details/${id}`);
  }

  getEfbDetails(id: number): Observable<EfbFuelDetailsRecord> {
    return this.http.get<EfbFuelDetailsRecord>(`/api/flight/efb-details/${id}`);
  }

  getFuelSupplierDetails(id: number): Observable<FuelSupplierDetailsRecord[]> {
    return this.http.get<FuelSupplierDetailsRecord[]>(
      `/api/flight/fuel-supplier-details/${id}`
    );
  }

  deleteEfb(record: EfbFuelDetailsRecord): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.delete<void>(`/api/flight/efb-details/${record.EFB_ID}`, {
      headers,
    });
  }

  updateEfbDetails(record: EfbFuelDetailsRecord): Observable<void> {
    return this.http.put<void>('/api/flight/efb-details', record);
  }

  calculateEFB(record: CalculateEFBRequest): Observable<CalculateEfbResponse> {
    return this.http.post<CalculateEfbResponse>(
      '/api/flight/efb-details/calculate',
      record
    );
  }

  getAttachments(flightLegNumber: number): Observable<FlightAttachemnt[]> {
    return this.http.get<FlightAttachemnt[]>(
      `/api/flight/details/${flightLegNumber}/attachment`
    );
  }

  uploadAttachments(flightLegNumber: number, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach((file) => formData.append('attachments', file));

    return this.http.post<any>(
      `/api/flight/details/${flightLegNumber}/attachment`,
      formData
    );
  }

  getFlightAttachmentDownloadUrl(attachmentId: number): Observable<any> {
    return this.http.get<any>(
      `/api/flight/details/attachment/${attachmentId}/download-url`
    );
  }

  deleteAttachment(attachmentId: number): Observable<any> {
    return this.http.delete<any>(
      `/api/flight/details/attachment/${attachmentId}`
    );
  }

  addEfbDetails(record: any): Observable<void> {
    return this.http.post<void>('/api/flight/efb-details', record);
  }

  updateFsdDetails(record: FuelSupplierDetailsRecord): Observable<void> {
    return this.http.put<void>('/api/flight/fuel-supplier-details', record);
  }

  addFsdDetails(record: FuelSupplierDetailsRecord): Observable<void> {
    return this.http.post<void>('/api/flight/fuel-supplier-details', record);
  }

  deleteFsdDetails(record: FuelSupplierDetailsRecord): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const params = new HttpParams()
      .set('ID', record.ID!)
      .set('SourceTable', record.SourceTable);

    return this.http.delete<void>(`/api/flight/fuel-supplier-details`, {
      headers,
      params,
    });
  }
}
